import { lazy, Suspense, useState, type FunctionComponent } from "react";
import { DeleteOutlined, SendOutlined, SmileOutlined } from "@ant-design/icons";
import data from "@emoji-mart/data";
import emojiI18n from "@emoji-mart/data/i18n/pl.json";
import { Avatar, Button, Drawer, Empty, Input, Popconfirm, Popover, Space, Spin, Tag, Typography } from "antd";
import { type BaseEmoji } from "emoji-mart/dist-es/utils/emoji-index/nimble-emoji-index";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  MAX_CHALLENGE_COMMENT_LENGTH,
  MAX_CHALLENGE_COMMENT_MEDIA,
  type ChallengeFeedPost,
  type ChallengePostMedia,
} from "@fitness-app/data-models/entities/ChallengeFeed";

import ChallengeFeedMarkdown from "../ChallengeFeedMarkdown";
import { ChallengePostMediaPreview, ChallengePostMediaUploadButton } from "../ChallengePostMediaField";
import { ChallengeMediaGallery } from "./ChallengeMediaGallery";
import { useChallengePostComments } from "./useChallengeFeed";

/** Same full picker as the chat input — a desktop keyboard has no emoji key. Lazy: large data set. */
const Picker = lazy(() => import("@emoji-mart/react"));

dayjs.extend(relativeTime);

type Props = {
  post: ChallengeFeedPost | null;
  challengeId: string;
  onClose: () => void;
  onAddComment: (payload: { content: string; media: ChallengePostMedia[] }) => void;
  onDeleteComment: (commentId: string) => void;
  isSending: boolean;
};

/**
 * The post's thread — the trainer answers here and moderates participant comments. A drawer rather
 * than an inline accordion: a conversation deserves the whole column, and the feed keeps its place.
 */
const ChallengePostThreadDrawer: FunctionComponent<Props> = ({
  post,
  challengeId,
  onClose,
  onAddComment,
  onDeleteComment,
  isSending,
}) => {
  const [draft, setDraft] = useState("");
  const [media, setMedia] = useState<ChallengePostMedia[]>([]);
  const commentsQuery = useChallengePostComments(post?.id);

  const submit = () => {
    const content = draft.trim();
    if ((!content && !media.length) || isSending) {
      return;
    }
    onAddComment({ content, media });
    setDraft("");
    setMedia([]);
  };

  return (
    <Drawer
      open={Boolean(post)}
      onClose={onClose}
      width={560}
      title="Post i komentarze"
      destroyOnClose
      footer={
        <div>
          <ChallengePostMediaPreview value={media} onChange={setMedia} disabled={isSending} />
          <Space.Compact style={{ width: "100%", marginTop: media.length ? 12 : 0 }}>
            <Input
              value={draft}
              maxLength={MAX_CHALLENGE_COMMENT_LENGTH}
              placeholder="Odpowiedz uczestniczkom…"
              onChange={(event) => setDraft(event.target.value)}
              onPressEnter={submit}
            />
            <Popover
              trigger="click"
              styles={{ body: { padding: 0 } }}
              content={
                <Suspense fallback={<Spin />}>
                  <Picker
                    data={data}
                    i18n={emojiI18n}
                    onEmojiSelect={(emoji: BaseEmoji) => setDraft((current) => current + emoji.native)}
                  />
                </Suspense>
              }
            >
              <Button icon={<SmileOutlined />} />
            </Popover>
            <ChallengePostMediaUploadButton
              challengeId={challengeId}
              value={media}
              onChange={setMedia}
              disabled={isSending}
              limit={MAX_CHALLENGE_COMMENT_MEDIA}
            />
            <Button type="primary" icon={<SendOutlined />} loading={isSending} onClick={submit}>
              Wyślij
            </Button>
          </Space.Compact>
        </div>
      }
    >
      {post ? (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div>
            <Space size={8} align="center">
              <Avatar size={32} src={post.authorAvatar ?? undefined}>
                {post.authorName.charAt(0).toUpperCase()}
              </Avatar>
              <Space direction="vertical" size={0}>
                <Space size={6}>
                  <Typography.Text strong>{post.authorName}</Typography.Text>
                  {post.authorIsTrainer ? (
                    <Tag color="gold" bordered={false}>
                      Trener
                    </Tag>
                  ) : null}
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {dayjs(post.createdAt).fromNow()}
                </Typography.Text>
              </Space>
            </Space>
            <div className="mt-3">
              <ChallengeFeedMarkdown content={post.content} />
              {/* The drawer used to drop the post's attachments entirely — you opened a thread about
                  a photo and the photo was not there. */}
              <ChallengeMediaGallery media={post.media} />
            </div>
          </div>

          <div className="border-t border-[#f0f0f0] pt-4">
            <Typography.Text type="secondary" className="uppercase" style={{ fontSize: 12, letterSpacing: 0.6 }}>
              Komentarze ({post.commentCount})
            </Typography.Text>

            {commentsQuery.isLoading ? (
              <div className="py-6 text-center">
                <Spin />
              </div>
            ) : commentsQuery.data?.length ? (
              <Space direction="vertical" size={14} style={{ width: "100%", marginTop: 12 }}>
                {commentsQuery.data.map((comment) => (
                  <div key={comment.id} className="flex items-start gap-2">
                    <Avatar size={28} src={comment.authorAvatar ?? undefined}>
                      {comment.authorName.charAt(0).toUpperCase()}
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <Space size={6} align="center" wrap>
                        <Typography.Text strong>{comment.authorName}</Typography.Text>
                        {comment.authorIsTrainer ? (
                          <Tag color="gold" bordered={false}>
                            Trener
                          </Tag>
                        ) : null}
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {dayjs(comment.createdAt).fromNow()}
                        </Typography.Text>
                      </Space>
                      {comment.content ? <div className="whitespace-pre-wrap">{comment.content}</div> : null}
                      <ChallengeMediaGallery media={comment.media} variant="comment" />
                    </div>
                    <Popconfirm
                      title="Usunąć komentarz?"
                      okText="Usuń"
                      okButtonProps={{ danger: true }}
                      cancelText="Anuluj"
                      onConfirm={() => onDeleteComment(comment.id)}
                    >
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                ))}
              </Space>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak komentarzy" />
            )}
          </div>
        </Space>
      ) : null}
    </Drawer>
  );
};

export default ChallengePostThreadDrawer;
